import { useEntities, useEntity } from "@ui/hooks/serviceUtils";
import useDataManager, { DataManagerProps } from "@ui/hooks/useDataManager";
import { ISupplierBooking } from "./useSupplierBooking";
import { IEmployee } from "./useEmployee";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { DateTime } from "luxon";
import { useToast } from "@chakra-ui/react";
export interface IGuestBooking {
  id: string;
  transactionId: string;
  requestor: IEmployee;
  startDate: Date;
  endDate: Date;
  meal: string;
  quantity: number;
  remarks?: string;
  days?: string[];
  supplierBooking?: ISupplierBooking;
}

export interface IGuestBookingSingle {
  id: string;
  transactionId: string;
  requestor: IEmployee;
  date: Date;
  meal: string;
  quantity: number;
  remarks?: string;
  days?: string[];
  supplierBooking?: ISupplierBooking;
}

interface IGuestBookingUpdate {
  id: string;
  quantity: number;
  transactionId: string;
}

const key = "guestbooking";
const endpoint = "/api/guestbooking";

export const useGuestBookingsQuery = (
  props: DataManagerProps<IGuestBooking> = {},
) => useDataManager<IGuestBooking>(key, `${endpoint}/query`, props);

export const useGuestBookings = () => useEntities<IGuestBooking>(key, endpoint);

export const useGuestBooking = (
  transactionId: string | undefined,
  toast: boolean = true,
) =>
  useEntity<IGuestBooking>(
    key,
    endpoint,
    transactionId,
    toast
      ? {
          add: "Guest Booking added",
          update: "Guest Booking updated",
          delete: "Guest Booking deleted",
        }
      : undefined,
  );

interface GuestBookingFilter {
  requestorId: string;
  date: DateTime;
  meal: string;
}

export const useFilteredGuestBookings = (
  requestorId: string,
  date: DateTime,
  meal: string,
) => {
  const formattedDate =
    date instanceof DateTime
      ? date.toISODate()
      : date
        ? DateTime.fromJSDate(date).toISODate()
        : null;

  return useQuery<any, Error>({
    queryKey: [key, requestorId, date, meal],
    queryFn: async () => {
      const url = `${endpoint}/filter?requestorId=${requestorId}&date=${formattedDate}&meal=${meal}`;
      const { data } = await axios.get<GuestBookingFilter>(url);
      return data;
    },
    enabled: !!(requestorId && date && meal),
  });
};

export const useGuestBookingsById = (requestorId: string) => {
  return useQuery<any, Error>({
    queryKey: [key, requestorId],
    queryFn: async () => {
      const url = `${endpoint}/filter-by-id?requestorId=${requestorId}`;
      const { data } = await axios.get<IGuestBooking>(url);
      return data;
    },
    enabled: !!requestorId,
  });
};

export const useGuestBookingsByDateAndUser = (
  requestorId: string | undefined,
  date: DateTime | undefined,
) => {
  return useQuery<IGuestBooking[], Error>({
    queryKey: ["guestbookings-by-date-user", requestorId, date],
    queryFn: async () => {
      if (!requestorId || !date) throw new Error("Invalid filter params");

      const payload = {
        requestorId,
        date: date.toISODate(),
      };

      const { data } = await axios.post<IGuestBooking[]>(
        `${endpoint}/filter-by-date-user`,
        payload,
      );

      return data;
    },
    enabled: !!(requestorId && date),
  });
};

export const useGuestBookingsByTransactionId = (id: string | undefined) => {
  return useQuery<any, Error>({
    queryKey: ["booking", id],
    queryFn: async () => {
      const url = `${endpoint}/transaction?id=${id}`;
      const response = await axios.get<any>(url);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useGuestBookingMutations = () => {
  const queryClient = useQueryClient();
  const toast = useToast({
    duration: 2000,
    isClosable: true,
    position: "top",
  });

  const invalidateBookings = (transactionId: string) => {
    queryClient.invalidateQueries({ queryKey: ["booking", transactionId] });
  };

  const update = useMutation<any, AxiosError<any>, IGuestBookingUpdate>({
    mutationFn: async ({ id, quantity }: { id: string; quantity: number }) => {
      const { data: res } = await axios.put(`${endpoint}/booking/${id}`, {
        quantity,
      });
      return res;
    },
    onMutate: () => {
      toast.close(`update-toast-${key}`);
    },
    onSuccess: (_, variables) => {
      if (!toast.isActive(`update-toast-${key}`)) {
        toast({
          id: `update-toast-${key}`,
          title: `Booking Updated Successfully`,
          status: "success",
        });
      }
      invalidateBookings(variables.transactionId);
      queryClient.invalidateQueries({
        queryKey: [key, variables.transactionId],
      });
    },
    onError: (error) => {
      toast({
        id: `update-toast-${key}`,
        title: error?.response?.data ?? "Error updating booking",
        status: "error",
      });
    },
  });

  const remove = useMutation<
    any,
    AxiosError<any>,
    { id: string; transactionId: string }
  >({
    mutationFn: async ({ id }) => {
      const { data } = await axios.delete(`${endpoint}/booking/${id}`);
      return data;
    },
    onMutate: () => {
      toast.close(`delete-toast-${key}`);
    },
    onSuccess: (_, variables) => {
      if (!toast.isActive(`delete-toast-${key}`)) {
        toast({
          id: `delete-toast-${key}`,
          title: `Booking Updated Successfully`,
          status: "success",
        });
      }
      invalidateBookings(variables.transactionId);
      queryClient.invalidateQueries({
        queryKey: [key, variables.transactionId],
      });
    },
    onError: (error) => {
      toast({
        id: `delete-toast-${key}`,
        title: error?.response?.data ?? "Error deleting booking",
        status: "error",
      });
    },
  });

  return { update, remove };
};
